import React, {useEffect, useMemo, useState} from 'react'
import {toast} from 'react-toastify'
import {
  getAdminUserPermissionMatrix,
  updateAdminUserOverrides,
  type AdminUserAccessOverride,
  type AdminUserPermissionMatrixRow,
} from '../core/accessRequests'

import {
  productionModuleOrderIndex,
  resolveAuthoritativePermissionPlacement,
} from '../core/productionPermissionGrouping'
import type {PermissionCatalogEntry} from '../core/permissionGrouping'

type RowState = AdminUserPermissionMatrixRow & {
  draftAllow: boolean
  draftDeny: boolean
}

type TriState = 'unchecked' | 'indeterminate' | 'checked'

/** Rows eligible for bulk override edits (extend when backend adds override flags). */
const isRowEligibleForBulk = (_row: RowState): boolean => true

const computeTriState = (
  sectionRows: RowState[],
  isActive: (row: RowState) => boolean
): TriState => {
  const eligible = sectionRows.filter(isRowEligibleForBulk)
  if (eligible.length === 0) return 'unchecked'
  const activeCount = eligible.filter(isActive).length
  if (activeCount === 0) return 'unchecked'
  if (activeCount === eligible.length) return 'checked'
  return 'indeterminate'
}

type SectionTriStateCheckboxProps = {
  state: TriState
  onToggle: (selectAll: boolean) => void
  ariaLabel: string
  title: string
}

const SectionTriStateCheckbox: React.FC<SectionTriStateCheckboxProps> = ({
  state,
  onToggle,
  ariaLabel,
  title,
}) => {
  const inputRef = React.useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = state === 'indeterminate'
    }
  }, [state])

  return (
    <input
      ref={inputRef}
      type='checkbox'
      className='form-check-input mt-1'
      checked={state === 'checked'}
      aria-label={ariaLabel}
      title={title}
      onChange={() => onToggle(state !== 'checked')}
    />
  )
}

type OverrideSnapshotRow = {
  permissionCode: string
  effect: 'ALLOW' | 'DENY'
  scope: string | null
}

export type UserPermissionOverridesModalProps = {
  open: boolean
  userId: number
  userLabel: string
  onClose: () => void
  onSaved?: () => void
}

const sourceLabel = (src: string | null | undefined): string => {
  switch ((src || '').toUpperCase()) {
    case 'ROLE_DEFAULT':
      return 'Role default'
    case 'TENANT_ROLE_OVERRIDE':
      return 'Tenant role override'
    case 'CREW_RANK':
      return 'Crew rank'
    case 'PACKAGE':
      return 'Package'
    case 'USER_ALLOW':
      return 'Extra allow'
    case 'USER_DENY':
      return 'Deny override'
    case 'SCOPE_CLAMPED':
      return 'Scope clamped'
    case 'NONE':
      return '—'
    default:
      return src || '—'
  }
}

export const UserPermissionOverridesModal: React.FC<UserPermissionOverridesModalProps> = ({
  open,
  userId,
  userLabel,
  onClose,
  onSaved,
}) => {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [search, setSearch] = useState('')
  const [rows, setRows] = useState<RowState[]>([])
  const [initialSnapshot, setInitialSnapshot] = useState('')

  const normalizeCode = (value: string) => String(value || '').trim().toUpperCase()

  const buildSnapshot = (stateRows: RowState[]): string => {
    const normalized: OverrideSnapshotRow[] = []
    for (const row of stateRows) {
      const code = normalizeCode(row.permissionCode)
      if (!code) continue
      if (row.draftAllow) {
        normalized.push({permissionCode: code, effect: 'ALLOW', scope: null})
      } else if (row.draftDeny) {
        normalized.push({permissionCode: code, effect: 'DENY', scope: null})
      }
    }
    normalized.sort((a, b) => a.permissionCode.localeCompare(b.permissionCode))
    return JSON.stringify(normalized)
  }

  useEffect(() => {
    if (!open) return
    let cancelled = false
    ;(async () => {
      setLoading(true)
      setSearch('')
      try {
        const data = await getAdminUserPermissionMatrix(userId)
        if (cancelled) return
        setRows(
          data.map((r) => ({
            ...r,
            draftAllow: r.userAllowOverride,
            draftDeny: r.userDenyOverride,
          }))
        )
        const seedRows = data.map((r) => ({
          ...r,
          draftAllow: r.userAllowOverride,
          draftDeny: r.userDenyOverride,
        }))
        setInitialSnapshot(buildSnapshot(seedRows))
      } catch (e: any) {
        toast.error('Failed to load permission matrix')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [open, userId])

  const filtered = useMemo(() => {
    const q = search.trim().toUpperCase()
    if (!q) return rows
    return rows.filter(
      (r) =>
        r.permissionCode.toUpperCase().includes(q) ||
        (r.moduleCategory || '').toUpperCase().includes(q) ||
        (r.menuLabel || '').toUpperCase().includes(q) ||
        (r.subMenuLabel || '').toUpperCase().includes(q)
    )
  }, [rows, search])

  const currentSnapshot = useMemo(() => buildSnapshot(rows), [rows])
  const hasUnsavedChanges = useMemo(() => {
    if (loading) return false
    return currentSnapshot !== initialSnapshot
  }, [currentSnapshot, initialSnapshot, loading])

  const grouped = useMemo(() => {
    const catalogLike: PermissionCatalogEntry[] = rows.map((r) => ({
      permissionId: r.permissionId ?? -1,
      permissionCode: r.permissionCode,
      displayName: r.displayName ?? null,
      description: r.description ?? null,
      menuLabel: r.menuLabel ?? null,
      subMenuLabel: r.subMenuLabel ?? null,
      moduleCategory: r.moduleCategory ?? null,
      sortOrder: null,
    }))
    type G = {key: string; items: RowState[]; pl: ReturnType<typeof resolveAuthoritativePermissionPlacement>}
    const buckets = new Map<string, G>()
    for (const r of filtered) {
      const entry: PermissionCatalogEntry = {
        permissionId: r.permissionId ?? -1,
        permissionCode: r.permissionCode,
        displayName: r.displayName ?? null,
        description: r.description ?? null,
        menuLabel: r.menuLabel ?? null,
        subMenuLabel: r.subMenuLabel ?? null,
        moduleCategory: r.moduleCategory ?? null,
        sortOrder: null,
      }
      const pl = resolveAuthoritativePermissionPlacement(entry, catalogLike)
      const key =
        pl.subMenuLabel && pl.subMenuLabel !== '—' ?
          `${pl.moduleLabel} · ${pl.menuLabel} · ${pl.subMenuLabel}`
        : `${pl.moduleLabel} · ${pl.menuLabel}`
      const hit = buckets.get(key)
      if (!hit) buckets.set(key, {key, items: [r], pl})
      else hit.items.push(r)
    }
    return Array.from(buckets.values())
      .sort((a, b) => {
        const cmp = productionModuleOrderIndex(a.pl.moduleLabel) - productionModuleOrderIndex(b.pl.moduleLabel)
        if (cmp !== 0) return cmp
        if (a.pl.sortIndex !== b.pl.sortIndex) return a.pl.sortIndex - b.pl.sortIndex
        return a.key.localeCompare(b.key)
      })
      .map((b) => [b.key, b.items] as [string, RowState[]])
  }, [filtered, rows])

  const setAllow = (code: string, checked: boolean) => {
    setRows((prev) =>
      prev.map((r) =>
        r.permissionCode === code
          ? {...r, draftAllow: checked, draftDeny: checked ? false : r.draftDeny}
          : r
      )
    )
  }

  const setDeny = (code: string, checked: boolean) => {
    setRows((prev) =>
      prev.map((r) =>
        r.permissionCode === code
          ? {...r, draftDeny: checked, draftAllow: checked ? false : r.draftAllow}
          : r
      )
    )
  }

  const clearRowOverride = (code: string) => {
    setRows((prev) =>
      prev.map((r) => (r.permissionCode === code ? {...r, draftAllow: false, draftDeny: false} : r))
    )
  }

  const sectionPermissionCodes = (sectionRows: RowState[]) =>
    new Set(sectionRows.filter(isRowEligibleForBulk).map((r) => r.permissionCode))

  /** Bulk allow/deny applies to rows in the section slice passed in (search-narrowed when filter active). */
  const bulkSetAllowForSection = (sectionRows: RowState[], enable: boolean) => {
    const codes = sectionPermissionCodes(sectionRows)
    if (codes.size === 0) return
    setRows((prev) =>
      prev.map((r) => {
        if (!codes.has(r.permissionCode)) return r
        if (enable) return {...r, draftAllow: true, draftDeny: false}
        return {...r, draftAllow: false}
      })
    )
  }

  const bulkSetDenyForSection = (sectionRows: RowState[], enable: boolean) => {
    const codes = sectionPermissionCodes(sectionRows)
    if (codes.size === 0) return
    setRows((prev) =>
      prev.map((r) => {
        if (!codes.has(r.permissionCode)) return r
        if (enable) return {...r, draftDeny: true, draftAllow: false}
        return {...r, draftDeny: false}
      })
    )
  }

  const clearSectionOverrides = (sectionRows: RowState[]) => {
    const codes = sectionPermissionCodes(sectionRows)
    if (codes.size === 0) return
    setRows((prev) =>
      prev.map((r) =>
        codes.has(r.permissionCode) ? {...r, draftAllow: false, draftDeny: false} : r
      )
    )
  }

  const searchActive = search.trim().length > 0

  const save = async () => {
    if (saving || loading || !hasUnsavedChanges) return
    const overrides: AdminUserAccessOverride[] = []
    for (const r of rows) {
      if (r.draftAllow) overrides.push({permissionCode: r.permissionCode, effect: 'ALLOW'})
      else if (r.draftDeny) overrides.push({permissionCode: r.permissionCode, effect: 'DENY'})
    }
    setSaving(true)
    try {
      await updateAdminUserOverrides(userId, overrides)
      setInitialSnapshot(currentSnapshot)
      toast.success('Overrides saved')
      onSaved?.()
    } catch (e: any) {
      toast.error('Could not save overrides')
    } finally {
      setSaving(false)
    }
  }

  const handleCloseRequest = () => {
    if (saving) return
    if (hasUnsavedChanges) {
      const discard = window.confirm('Discard unsaved override changes?')
      if (!discard) return
    }
    onClose()
  }

  if (!open) return null

  return (
    <div
      className='modal d-block'
      style={{backgroundColor: 'rgba(0,0,0,0.35)', zIndex: 2050}}
      role='dialog'
      aria-modal='true'
    >
      <div className='modal-dialog modal-dialog-centered modal-xl modal-dialog-scrollable'>
        <div className='modal-content'>
          <div className='modal-header'>
            <h5 className='modal-title mb-0'>User permission overrides</h5>
            <button type='button' className='btn-close' aria-label='Close' onClick={handleCloseRequest} />
          </div>
          <div className='modal-body'>
            <p className='small text-muted mb-2'>
              <strong className='text-gray-900'>{userLabel}</strong> · user id {userId}.{' '}
              <strong>Role default</strong> comes from <code>role_permissions</code> for this person&apos;s role.{' '}
              <strong>Extra allow</strong> and <strong>Deny override</strong> are saved to user overrides; deny wins. Clear
              both checkboxes on a row to fall back to role/packages only. Effective access also considers access
              packages — refresh in Role Permissions after changes if needed. Section header checkboxes bulk-apply to
              permissions currently visible in that section{searchActive ? ' (search is narrowing scope)' : ''}; save
              once with <strong>Save overrides</strong>.
            </p>
            <div className='mb-3'>
              <input
                className='form-control'
                placeholder='Search permission code or category…'
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            {loading ? (
              <div className='text-center py-5 text-muted'>Loading permission matrix…</div>
            ) : (
              <div className='border rounded'>
                {grouped.map(([category, catRows]) => {
                  const allowState = computeTriState(catRows, (r) => r.draftAllow)
                  const denyState = computeTriState(catRows, (r) => r.draftDeny)
                  const eligibleCount = catRows.filter(isRowEligibleForBulk).length
                  const bulkScopeHint =
                    searchActive ?
                      `Apply to ${eligibleCount} visible permission(s) in this section`
                    : `Apply to all ${eligibleCount} permission(s) in this section`

                  return (
                  <div key={category}>
                    <table className='table table-sm table-row-dashed mb-0 align-middle'>
                      <thead>
                        <tr className='text-gray-800 small border-bottom bg-light'>
                          <th className='fw-bold text-uppercase align-middle'>{category}</th>
                          <th className='text-center align-middle' style={{width: '7rem'}}>
                            Role default
                          </th>
                          <th className='text-center align-middle' style={{width: '7rem'}}>
                            <div>Extra allow</div>
                            <SectionTriStateCheckbox
                              state={allowState}
                              ariaLabel={`${allowState === 'checked' ? 'Clear' : 'Select'} all extra allow in ${category}`}
                              title={`${bulkScopeHint}. ${allowState === 'checked' ? 'Clear' : 'Select'} all extra allow.`}
                              onToggle={(selectAll) => bulkSetAllowForSection(catRows, selectAll)}
                            />
                          </th>
                          <th className='text-center align-middle' style={{width: '7rem'}}>
                            <div>Deny override</div>
                            <SectionTriStateCheckbox
                              state={denyState}
                              ariaLabel={`${denyState === 'checked' ? 'Clear' : 'Select'} all deny override in ${category}`}
                              title={`${bulkScopeHint}. ${denyState === 'checked' ? 'Clear' : 'Select'} all deny override.`}
                              onToggle={(selectAll) => bulkSetDenyForSection(catRows, selectAll)}
                            />
                          </th>
                          <th className='text-center align-middle' style={{width: '8rem'}}>
                            Effective access
                          </th>
                          <th className='text-center align-middle' style={{width: '7rem'}}>
                            Source
                          </th>
                          <th className='text-end align-middle' style={{width: '6.5rem'}}>
                            <button
                              type='button'
                              className='btn btn-link btn-sm py-0 text-nowrap'
                              title={`${bulkScopeHint}. Clear both extra allow and deny override.`}
                              onClick={() => clearSectionOverrides(catRows)}
                            >
                              Clear section
                            </button>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {catRows.map((r) => {
                          const dirty =
                            r.draftAllow !== r.userAllowOverride || r.draftDeny !== r.userDenyOverride
                          const hasDraft = r.draftAllow || r.draftDeny
                          return (
                            <tr key={r.permissionCode}>
                              <td className='font-monospace small text-gray-900'>{r.permissionCode}</td>
                              <td className='text-center'>
                                <input
                                  type='checkbox'
                                  className='form-check-input'
                                  checked={r.inheritedFromRole}
                                  disabled
                                  title='Granted via the assigned role’s default permissions'
                                />
                              </td>
                              <td className='text-center'>
                                <input
                                  type='checkbox'
                                  className='form-check-input'
                                  checked={r.draftAllow}
                                  onChange={(e) => setAllow(r.permissionCode, e.target.checked)}
                                />
                              </td>
                              <td className='text-center'>
                                <input
                                  type='checkbox'
                                  className='form-check-input'
                                  checked={r.draftDeny}
                                  onChange={(e) => setDeny(r.permissionCode, e.target.checked)}
                                />
                              </td>
                              <td className='text-center small'>
                                <span className={r.effectiveAllowed ? 'text-success fw-semibold' : 'text-muted'}>
                                  {r.effectiveAllowed ? 'Allowed' : 'Denied'}
                                </span>
                                {dirty && (
                                  <div className='text-warning' style={{fontSize: '0.7rem'}}>
                                    Recalculates on save
                                  </div>
                                )}
                              </td>
                              <td className='text-center small text-gray-700'>
                                {sourceLabel(r.effectiveSource)}
                              </td>
                              <td className='text-end'>
                                <button
                                  type='button'
                                  className='btn btn-link btn-sm py-0'
                                  disabled={!hasDraft}
                                  onClick={() => clearRowOverride(r.permissionCode)}
                                >
                                  Reset row
                                </button>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                  )
                })}
              </div>
            )}
          </div>
          <div className='modal-footer'>
            <button type='button' className='btn btn-light' disabled={saving} onClick={handleCloseRequest}>
              Cancel
            </button>
            <button
              type='button'
              className='btn btn-primary'
              disabled={saving || loading || !hasUnsavedChanges}
              onClick={save}
            >
              {saving ? 'Saving...' : 'Save overrides'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
